import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Send, RefreshCw, Database } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import aiChatService from '@/lib/aiChatService';

const formatNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString() : '0';
};

const formatCurrency = (value) => {
  const parsed = Number(value);
  const safeValue = Number.isFinite(parsed) ? parsed : 0;
  return `$${safeValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
};

const renderInlineText = (text, keyPrefix) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    const isBold = part.startsWith('**') && part.endsWith('**');
    const content = isBold ? part.slice(2, -2) : part;
    return isBold ? (
      <strong key={`${keyPrefix}-bold-${index}`} className="font-semibold">
        {content}
      </strong>
    ) : (
      <span key={`${keyPrefix}-text-${index}`}>{content}</span>
    );
  });
};

const renderMessageContent = (content, keyPrefix) => {
  const lines = content.split('\n');
  const blocks = [];
  let paragraph = [];
  let orderedList = null;
  let currentOrderedItem = null;
  let unorderedList = null;

  const peekNextNonEmpty = (startIndex) => {
    for (let i = startIndex; i < lines.length; i += 1) {
      const nextLine = lines[i].trim();
      if (nextLine) return nextLine;
    }
    return null;
  };

  const closeParagraph = () => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(' ').trim();
    if (text) {
      blocks.push(
        <p key={`${keyPrefix}-p-${blocks.length}`} className="leading-relaxed">
          {renderInlineText(text, `${keyPrefix}-p-${blocks.length}`)}
        </p>
      );
    }
    paragraph = [];
  };

  const closeUnorderedList = () => {
    if (!unorderedList || unorderedList.items.length === 0) {
      unorderedList = null;
      return;
    }
    blocks.push(
      <ul key={`${keyPrefix}-ul-${blocks.length}`} className="ml-5 list-disc space-y-1">
        {unorderedList.items.map((item, index) => (
          <li key={`${keyPrefix}-ul-${index}`}>
            {renderInlineText(item, `${keyPrefix}-ul-${index}`)}
          </li>
        ))}
      </ul>
    );
    unorderedList = null;
  };

  const closeOrderedList = () => {
    if (!orderedList || orderedList.items.length === 0) {
      orderedList = null;
      currentOrderedItem = null;
      return;
    }
    blocks.push(
      <ol key={`${keyPrefix}-ol-${blocks.length}`} className="ml-5 list-decimal space-y-2">
        {orderedList.items.map((item, index) => (
          <li key={`${keyPrefix}-ol-${index}`} className="space-y-2">
            <div>{renderInlineText(item.text, `${keyPrefix}-ol-${index}`)}</div>
            {item.bullets.length > 0 && (
              <ul className="ml-5 list-disc space-y-1">
                {item.bullets.map((bullet, bulletIndex) => (
                  <li key={`${keyPrefix}-ol-${index}-bullet-${bulletIndex}`}>
                    {renderInlineText(
                      bullet,
                      `${keyPrefix}-ol-${index}-bullet-${bulletIndex}`
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    );
    orderedList = null;
    currentOrderedItem = null;
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    const orderedMatch = line.match(/^\d+[.)]\s+(.*)$/);
    const unorderedMatch = line.match(/^[-*•]\s+(.*)$/);

    if (!line) {
      closeParagraph();
      const nextLine = peekNextNonEmpty(index + 1);
      if (unorderedList && (!nextLine || !nextLine.match(/^[-*•]\s+/))) {
        closeUnorderedList();
      }
      if (
        orderedList &&
        (!nextLine || !nextLine.match(/^(\d+[.)]\s+|[-*•]\s+)/))
      ) {
        closeOrderedList();
      }
      return;
    }

    if (orderedMatch) {
      closeParagraph();
      closeUnorderedList();
      if (!orderedList) {
        orderedList = { items: [] };
      }
      currentOrderedItem = {
        text: orderedMatch[1].trim(),
        bullets: []
      };
      orderedList.items.push(currentOrderedItem);
      return;
    }

    if (unorderedMatch) {
      closeParagraph();
      if (orderedList && currentOrderedItem) {
        currentOrderedItem.bullets.push(unorderedMatch[1].trim());
      } else {
        if (!unorderedList) {
          unorderedList = { items: [] };
        }
        unorderedList.items.push(unorderedMatch[1].trim());
      }
      return;
    }

    if (orderedList && currentOrderedItem) {
      currentOrderedItem.text = `${currentOrderedItem.text} ${line}`.trim();
      return;
    }

    if (unorderedList && unorderedList.items.length > 0) {
      const lastIndex = unorderedList.items.length - 1;
      unorderedList.items[lastIndex] = `${unorderedList.items[lastIndex]} ${line}`.trim();
      return;
    }

    paragraph.push(line);
  });

  closeParagraph();
  closeUnorderedList();
  closeOrderedList();

  return <div className="space-y-2">{blocks}</div>;
};

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: 'Hi! I can analyze your church data and suggest next steps. Ask about attendance, giving, events, tasks, or engagement.'
};

export function AIChat() {
  const { toast } = useToast();
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(true);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [starters, setStarters] = useState(() => aiChatService.buildStarters(null));
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadSnapshot = useCallback(async ({ forceRefresh = false } = {}) => {
    setIsLoadingSnapshot(true);
    setSnapshotError(null);

    try {
      const dataSnapshot = await aiChatService.getSnapshot({ forceRefresh });
      setSnapshot(dataSnapshot);
      setStarters(aiChatService.buildStarters(dataSnapshot));
    } catch (error) {
      console.error('Failed to load AI chat snapshot:', error);
      setSnapshot(null);
      setStarters(aiChatService.buildStarters(null));
      setSnapshotError(error.message || 'Unable to load snapshot');
    } finally {
      setIsLoadingSnapshot(false);
    }
  }, []);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const handleSend = async (value) => {
    const trimmed = value.trim();
    if (!trimmed || isSending) return;

    const userMessage = { role: 'user', content: trimmed };
    const nextMessages = [...messages, userMessage];

    setMessages(nextMessages);
    setInput('');
    setIsSending(true);

    try {
      const reply = await aiChatService.sendMessage({
        messages: nextMessages,
        snapshot
      });

      setMessages([...nextMessages, { role: 'assistant', content: reply }]);
    } catch (error) {
      console.error('AI chat request failed:', error);
      toast({
        title: 'AI chat unavailable',
        description: error.message || 'Please try again in a moment.',
        variant: 'destructive'
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleStarterClick = (starter) => {
    setInput(starter);
    inputRef.current?.focus();
  };

  const handleClearChat = () => {
    setMessages([INITIAL_MESSAGE]);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend(input);
    }
  };

  const snapshotUpdatedAt = useMemo(() => {
    if (!snapshot?.generatedAt) return 'Not available';
    return new Date(snapshot.generatedAt).toLocaleString();
  }, [snapshot]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold text-foreground">AI Church Assistant</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Ask questions about your church data and get actionable guidance. Suggested starters update based on your latest snapshot.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="flex flex-col">
          <CardHeader className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Chat with your data
                </CardTitle>
                <CardDescription>
                  Responses use the snapshot on the right. Ask for insights, priorities, or next steps.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleClearChat}>
                  Clear chat
                </Button>
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {starters.map((starter) => (
                <Button
                  key={starter}
                  variant="outline"
                  size="sm"
                  className="h-auto w-full justify-start whitespace-normal text-left leading-snug py-2"
                  onClick={() => handleStarterClick(starter)}
                >
                  {starter}
                </Button>
              ))}
            </div>
          </CardHeader>

          <CardContent className="flex flex-1 flex-col gap-4">
            <div className="flex-1 rounded-lg border bg-muted/30 p-4">
              <div className="space-y-4 max-h-[420px] overflow-y-auto pr-2">
                {messages.map((message, index) => (
                  <div
                    key={`${message.role}-${index}`}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card text-foreground border'
                      }`}
                    >
                      {message.role === 'assistant'
                        ? renderMessageContent(message.content, `msg-${index}`)
                        : message.content}
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="rounded-lg border bg-card px-3 py-2 text-sm text-muted-foreground">
                      Analyzing your data...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about attendance, giving, events, tasks, or engagement..."
                className="min-h-[80px] flex-1"
              />
              <Button
                onClick={() => handleSend(input)}
                disabled={isSending || !input.trim()}
                className="sm:w-32"
              >
                <Send className="mr-2 h-4 w-4" />
                Send
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-primary" />
                  Data Snapshot
                </CardTitle>
                <CardDescription>Updated {snapshotUpdatedAt}</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadSnapshot({ forceRefresh: true })}
                disabled={isLoadingSnapshot}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoadingSnapshot ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {isLoadingSnapshot && (
              <p className="text-muted-foreground">Loading snapshot...</p>
            )}

            {!isLoadingSnapshot && snapshotError && (
              <p className="text-sm text-red-600">{snapshotError}</p>
            )}

            {!isLoadingSnapshot && snapshot && (
              <div className="space-y-3">
                <div>
                  <p className="font-medium">Members</p>
                  <p className="text-muted-foreground">
                    {formatNumber(snapshot.members.total)} total, {formatNumber(snapshot.members.active)} active,
                    {` ${formatNumber(snapshot.members.inactive)} inactive`}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Giving</p>
                  <p className="text-muted-foreground">
                    {formatCurrency(snapshot.donations.monthly)} this month ({formatNumber(snapshot.donations.growthRate)}% trend)
                  </p>
                </div>
                {snapshot.donors?.topDonors?.length > 0 && (
                  <div>
                    <p className="font-medium">Top givers</p>
                    <p className="text-muted-foreground">
                      {snapshot.donors.topDonors
                        .slice(0, 2)
                        .map((donor) => `${donor.name} (${formatCurrency(donor.totalAmount)})`)
                        .join(', ')}
                    </p>
                  </div>
                )}
                <div>
                  <p className="font-medium">Attendance</p>
                  <p className="text-muted-foreground">
                    Sunday participation {formatNumber(snapshot.attendance.sundayServiceRate)}%,
                    avg {formatNumber(snapshot.attendance.sundayServiceAverage)} attendees
                  </p>
                </div>
                <div>
                  <p className="font-medium">Events</p>
                  <p className="text-muted-foreground">
                    {formatNumber(snapshot.events.upcoming)} upcoming, {formatNumber(snapshot.events.needingVolunteers)} need volunteers
                  </p>
                </div>
                <div>
                  <p className="font-medium">Tasks</p>
                  <p className="text-muted-foreground">
                    {formatNumber(snapshot.tasks.pending)} pending, {formatNumber(snapshot.tasks.overdue)} overdue
                  </p>
                </div>
                <div>
                  <p className="font-medium">Engagement</p>
                  <p className="text-muted-foreground">
                    {formatNumber(snapshot.sms.activeConversations)} active SMS conversations,
                    {` ${formatNumber(snapshot.celebrations.totalUpcoming)} celebrations coming up`}
                  </p>
                </div>
              </div>
            )}

            {!isLoadingSnapshot && !snapshot && !snapshotError && (
              <p className="text-muted-foreground">No snapshot available yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
